/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */
import { Inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  ROLE_WILDCARD_ONLYUSERS,
  ROLE_WILDCARD_PUBLIC,
  UnespCoreEnvironment,
  UnespCoreRolesRoute,
} from '../../interfaces'
import { BehaviorSubject, finalize, Observable } from 'rxjs'
import { Router } from '@angular/router'
import { UnespCoreMenuNavigationService } from '../unesp-core-menu-navigation'
import { UnespCoreMessageService } from '../unesp-core-message'
import { UnespCoreUserService } from '../unesp-core-user'
import { AuthService } from 'src/app/services/auth.service'

/**
 * @description
 *
 * Serviço responsável por se comunicar com a Central de Acessos e definir usuário para a aplicação.
 *
 * - Efetua autorização na central de acessos
 * - Aciona serviço UnespCoreUserService para armazenar o usuário autorizado
 * - Realiza verificações de permissões baseadas em roles
 */
@Injectable({
  providedIn: 'root',
})
export class UnespCoreAuthService {
  public clientId
  public redirectUri
  public authServer
  public resourceServer
  public tokenDateKey

  public refreshTokenExpirationInSeconds?: number
  private _aquiredTokenTimeLeft$ = new BehaviorSubject<number | null>(null)
  get aquiredTokenTimeLeft$(): Observable<number | null> {
    return this._aquiredTokenTimeLeft$
  }

  constructor(
    protected http: HttpClient,
    protected unespCoreUserService: UnespCoreUserService,
    protected router: Router,
    protected unespCoreMenuNavigationService: UnespCoreMenuNavigationService,
    protected unespCoreMessageService: UnespCoreMessageService,
    private authService: AuthService,
    @Inject('environment') protected environment: UnespCoreEnvironment
  ) {
    this.clientId = environment.clientId
    this.redirectUri = environment.baseUrlRedirect
    this.authServer = environment.oauthAuthorize
    this.resourceServer = environment.baseUrlApi
    this.refreshTokenExpirationInSeconds = environment.refreshTokenExpirationInSeconds
    this.tokenDateKey = this.unespCoreUserService.getLocalStoragePrefix() + '_tokendate'
  }

  /**
   * Realiza logout do usuário na aplicação. O backend é acionado para remoção dos cookies de sessão do qual
   * o javascript não tem acesso.
   *
   * @param {string} mensagem Mensagem a ser exibida ao usuário.
   * @param {boolean} error Indica se a mensagem representa um erro ou não.
   */
  logout(message: string, error: boolean = false, timeout = 100): void {
    this.http
      .get(`${this.resourceServer}/auth/logout`)
      .pipe(
        finalize(() => {
          this.unespCoreUserService.removeUser()
          this.unespCoreMenuNavigationService.refreshMenu()
          this.clearXSRFToken()
          this.router.navigate([''])

          // Add some timout for the user to read the message, as logging out will redirect him
          setTimeout(() => {
            this.authService.logout()
          }, timeout)
        })
      )
      .subscribe({
        next: () => {
          if (error) {
            this.unespCoreMessageService.showMessageError(message)
          } else {
            this.unespCoreMessageService.showMessageSuccess(message)
          }
        },
        error: () => {
          if (error) {
            this.unespCoreMessageService.showMessageError(message)
          } else {
            this.unespCoreMessageService.showMessageSuccess(message)
          }
        },
      })
  }

  clearXSRFToken() {
    document.cookie = 'XSRF-TOKEN=; Max-Age=0; path=/'
  }

  /**
   * Verifica se o usuário tem alguma das roles presentes na lista de roles autorizadas enviadas no parâmetro rolesRoute
   *
   * @param { UnespCoreRolesRoute } rolesRoute Lista de roles autorizadas a ser comparada com as roles do usuário
   */
  checkAuthorizedRole(rolesRoute: UnespCoreRolesRoute): boolean {
    const usuario = this.unespCoreUserService.getUser()

    // Role public, route não precisa de usuário
    if (rolesRoute.allowedRoles.includes(ROLE_WILDCARD_PUBLIC)) return true

    // Só avança se houver usuário logado
    if (!usuario || !usuario.authorities) {
      return false
    }

    // Role Only Users, route precisa de usuário (mesmo sem roles)
    if (rolesRoute.allowedRoles.includes(ROLE_WILDCARD_ONLYUSERS)) return true

    // Verifica se o usuário tem alguma das roles autorizadas
    return usuario.authorities.some(authority => rolesRoute.allowedRoles.includes(authority))
  }

  /**
   * Verifica se o usuário tem alguma das roles presentes na lista de roles autorizadas enviadas no parâmetro requiredRoles
   *
   * @param { string[] } requiredRoles Lista de roles autorizadas a ser comparada com as roles do usuário
   */
  hasPermissionBasedRoles(requiredRoles: string[]): boolean {
    const usuario = this.unespCoreUserService.getUser()

    if (usuario) {
      return usuario.roles.some(role => requiredRoles.indexOf(role) !== -1)
    }

    return false
  }
}
